// FILE: src/pages/Mobile/MobileClockPage.js
// Clock In / Clock Out screen for Android APK.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, Stack, Alert, Avatar,
  CircularProgress, TextField, Chip, Paper, LinearProgress
} from '@mui/material';
import {
  PlayArrow, Stop, CheckCircle, MyLocation,
  CameraAlt, PhotoCamera, LocationOn
} from '@mui/icons-material';
import { Modal } from '@mui/material';
import { Capacitor } from '@capacitor/core';
import toast from 'react-hot-toast';
import api from '../../api/axios';
import { useAuth } from '../../context/AuthContext';
import { getNativeLocation, getNativeSelfie } from '../../utils/nativeAttendance';

const getTodayDate = () => {
  const now = new Date();
  return new Date(now - now.getTimezoneOffset() * 60000).toISOString().split('T')[0];
};

const isLateNow = () => {
  const now = new Date();
  return now.getHours() > 9 || (now.getHours() === 9 && now.getMinutes() > 40);
};

// Browser camera modal — fallback if somehow used on web
const CameraModal = ({ open, onCapture, onClose }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState('');
  const [capturing, setCapturing] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError('');
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then(s => { streamRef.current = s; setStream(s); if (videoRef.current) videoRef.current.srcObject = s; })
      .catch(() => setError('Camera access denied.'));
    return () => { if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; } };
  }, [open]);

  const stopStream = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setStream(null);
  };

  const handleCapture = () => {
    if (!videoRef.current || !stream) return;
    setCapturing(true);
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth || 640;
    canvas.height = videoRef.current.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.translate(canvas.width, 0); ctx.scale(-1, 1);
    ctx.drawImage(videoRef.current, 0, 0);
    canvas.toBlob(blob => { stopStream(); setCapturing(false); onCapture(blob); }, 'image/jpeg', 0.9);
  };

  return (
    <Modal open={open} onClose={() => { stopStream(); onClose(); }}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '96vw', bgcolor: '#0f172a', borderRadius: 3, p: 2, outline: 'none'
      }}>
        <Typography color="white" fontWeight={700} textAlign="center" mb={1.5}>
          <CameraAlt sx={{ mr: 1, verticalAlign: 'middle' }} /> Take Selfie
        </Typography>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : (
          <Box sx={{ borderRadius: 2, overflow: 'hidden', mb: 2, aspectRatio: '4/3' }}>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
            {!stream && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress sx={{ color: 'white' }} /></Box>}
          </Box>
        )}
        <Stack direction="row" spacing={1.5}>
          <Button fullWidth variant="outlined" sx={{ color: 'white', borderColor: '#475569' }} onClick={() => { stopStream(); onClose(); }}>Cancel</Button>
          <Button fullWidth variant="contained" color="success"
            startIcon={capturing ? <CircularProgress size={16} color="inherit" /> : <PhotoCamera />}
            disabled={!stream || !!error || capturing} onClick={handleCapture} sx={{ fontWeight: 700 }}>
            {capturing ? 'Saving...' : 'Take Photo'}
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
};

const MobileClockPage = () => {
  const { user } = useAuth();

  const [status,        setStatus]        = useState('OUT');
  const [todayRecord,   setTodayRecord]   = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionStep,    setActionStep]    = useState('');
  const [lateReason,    setLateReason]    = useState('');
  const [selfiePreview, setSelfiePreview] = useState('');
  const [gpsLocation,   setGpsLocation]   = useState(null);
  const [cameraOpen,    setCameraOpen]    = useState(false);
  const [clockAction,   setClockAction]   = useState('in');
  const [clockError,    setClockError]    = useState('');
  const [locationError, setLocationError] = useState('');
  const [workingSeconds,setWorkingSeconds]= useState(0);

  const todayStr = getTodayDate();

  const formatTime = (s) => {
    const h = String(Math.floor(s / 3600)).padStart(2, '0');
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return `${h}:${m}:${sec}`;
  };

  const fetchToday = useCallback(async () => {
    if (!user?.employeeId) return;
    setLoading(true);
    try {
      const res = await api.get(`/attendance/employee/${user.employeeId}`);
      const records = res.data || [];
      const todayRec = records.find(r => r.date === todayStr);
      if (todayRec) {
        setTodayRecord(todayRec);
        setStatus(todayRec.clockOutTime ? 'OUT_DONE' : 'IN');
        if (!todayRec.clockOutTime && todayRec.clockInTime) {
          const now = new Date();
          const [h, m, s] = todayRec.clockInTime.split(':');
          const ci = new Date(); ci.setHours(+h, +m, +s);
          setWorkingSeconds(Math.max(0, Math.floor((now - ci) / 1000)));
        }
      } else {
        setStatus('OUT');
        setTodayRecord(null);
        setWorkingSeconds(0);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [user?.employeeId, todayStr]);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  useEffect(() => {
    if (status !== 'IN') return;
    const id = setInterval(() => setWorkingSeconds(p => p + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const startAction = async (action) => {
    setClockError(''); setLocationError('');

    if (action === 'in' && isLateNow() && (!lateReason || !lateReason.trim())) {
      setClockError('Please enter a reason for late login.');
      return;
    }

    setClockAction(action);
    setActionLoading(true);
    setActionStep('Getting location...');

    try {
      const loc = await getNativeLocation();

      if (loc.isMockLocation) {
        setClockError('🚫 Fake GPS detected. Disable mock location apps.');
        toast.error('Fake GPS blocked');
        setActionLoading(false); setActionStep('');
        return;
      }

      if (loc.accuracy !== null && loc.accuracy < 5) {
        setClockError('GPS accuracy too perfect — fake GPS suspected. Disable any mock apps.');
        setActionLoading(false); setActionStep('');
        return;
      }

      setGpsLocation(loc);
      setActionStep('Opening camera...');

      if (Capacitor.isNativePlatform()) {
        const blob = await getNativeSelfie();
        if (blob) {
          if (selfiePreview) URL.revokeObjectURL(selfiePreview);
          setSelfiePreview(URL.createObjectURL(blob));
          setActionStep('Submitting...');
          await submit(blob, loc);
        }
      } else {
        setActionLoading(false); setActionStep('');
        setCameraOpen(true);
      }
    } catch (err) {
      setActionLoading(false); setActionStep('');
      setLocationError(err.message?.includes('permission')
        ? 'Permission denied. Go to Settings → Apps → HRMS → enable Location + Camera.'
        : 'Cannot get location. Make sure GPS is on.');
    }
  };

  const handleWebSelfie = async (blob) => {
    setCameraOpen(false);
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfiePreview(URL.createObjectURL(blob));
    setActionLoading(true); setActionStep('Submitting...');
    await submit(blob, gpsLocation);
  };

  const submit = async (blob, loc) => {
    setClockError('');
    try {
      const fd = new FormData();
      fd.append('selfie', blob, 'selfie.jpg');
      if (loc?.latitude != null) {
        fd.append('latitude',  String(loc.latitude));
        fd.append('longitude', String(loc.longitude));
        if (loc.accuracy != null) fd.append('gpsAccuracy', String(loc.accuracy));
        fd.append('isMockLocation', String(loc.isMockLocation || false));
      }
      if (clockAction === 'in' && isLateNow() && lateReason) fd.append('lateReason', lateReason);

      if (clockAction === 'in') {
        await api.post('/attendance/clock-in', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('✅ Clocked In!');
        setLateReason('');
      } else {
        await api.put('/attendance/clock-out', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('✅ Clocked Out!');
      }
      await fetchToday();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || '';
      setClockError(msg || (clockAction === 'in' ? 'Clock-in failed.' : 'Clock-out failed.'));
      toast.error('Failed');
      if (selfiePreview) URL.revokeObjectURL(selfiePreview);
      setSelfiePreview('');
    } finally {
      setActionLoading(false); setActionStep('');
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
      <CircularProgress size={48} />
    </Box>
  );

  const mapUrl = gpsLocation?.latitude
    ? `https://www.google.com/maps?q=${gpsLocation.latitude},${gpsLocation.longitude}` : null;

  const savedMapUrl = todayRecord?.clockInLatitude
    ? `https://www.google.com/maps?q=${todayRecord.clockInLatitude},${todayRecord.clockInLongitude}` : null;

  return (
    <Box sx={{ p: 2, maxWidth: 480, mx: 'auto' }}>

      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography fontSize={13} color="text.secondary">{todayStr}</Typography>
        <Typography fontSize={22} fontWeight={900}>
          Hi, {user?.username?.split(' ')[0]} 👋
        </Typography>
      </Box>

      {/* Status card */}
      <Paper sx={{
        p: 2.5, mb: 2, borderRadius: 3,
        background: status === 'IN'
          ? 'linear-gradient(135deg, #052e16, #14532d)'
          : status === 'OUT_DONE'
          ? 'linear-gradient(135deg, #1e3a5f, #1e293b)'
          : 'linear-gradient(135deg, #1e293b, #0f172a)'
      }}>
        <Typography color={status === 'IN' ? '#4ade80' : '#94a3b8'} fontWeight={800} fontSize={18}>
          {status === 'IN'
            ? '🟢 Clocked In'
            : status === 'OUT_DONE'
            ? '✅ Day Complete'
            : '🔴 Not Clocked In'}
        </Typography>

        {status === 'IN' && (
          <Typography color="#86efac" fontSize={13} mt={0.5}>
            ⏱ Working: <b>{formatTime(workingSeconds)}</b>
          </Typography>
        )}

        {todayRecord?.clockInTime && (
          <Stack direction="row" spacing={2} mt={1}>
            <Typography color="#94a3b8" fontSize={12}>
              In: <b style={{ color: '#e2e8f0' }}>{todayRecord.clockInTime?.slice(0, 5)}</b>
            </Typography>
            {todayRecord?.clockOutTime && (
              <Typography color="#94a3b8" fontSize={12}>
                Out: <b style={{ color: '#e2e8f0' }}>{todayRecord.clockOutTime?.slice(0, 5)}</b>
              </Typography>
            )}
          </Stack>
        )}

        {savedMapUrl && (
          <Chip
            icon={<LocationOn sx={{ fontSize: '13px !important' }} />}
            label="View location on map"
            size="small"
            component="a"
            href={savedMapUrl}
            target="_blank"
            rel="noopener noreferrer"
            clickable
            sx={{ mt: 1.5, bgcolor: '#0284c7', color: 'white', fontSize: 11, fontWeight: 600 }}
          />
        )}
      </Paper>

      {/* Loading bar */}
      {actionLoading && (
        <Box mb={2}>
          <Typography fontSize={12} color="text.secondary" mb={0.5}>{actionStep}</Typography>
          <LinearProgress color={clockAction === 'in' ? 'success' : 'error'} sx={{ borderRadius: 2, height: 5 }} />
        </Box>
      )}

      {/* GPS live indicator */}
      {gpsLocation?.latitude && mapUrl && (
        <Stack direction="row" alignItems="center" spacing={0.5} mb={1.5}
          component="a" href={mapUrl} target="_blank" rel="noopener noreferrer"
          sx={{ textDecoration: 'none' }}>
          <MyLocation sx={{ fontSize: 14, color: '#16a34a' }} />
          <Typography fontSize={11} color="#16a34a" sx={{ textDecoration: 'underline' }}>
            📍 GPS locked ({gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}) — tap to verify
          </Typography>
        </Stack>
      )}

      {/* Errors */}
      {locationError && (
        <Alert severity="warning" onClose={() => setLocationError('')} sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}>
          {locationError}
        </Alert>
      )}
      {clockError && (
        <Alert severity="error" onClose={() => setClockError('')} sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}>
          {clockError}
        </Alert>
      )}

      {/* Late reason */}
      {status === 'OUT' && isLateNow() && (
        <Box mb={2}>
          <Typography fontSize={13} mb={0.5} color="error.main" fontWeight={600}>
            ⏰ You are late — reason required:
          </Typography>
          <TextField fullWidth size="small" value={lateReason}
            onChange={e => setLateReason(e.target.value)}
            placeholder="e.g. Traffic, Doctor visit..."
            sx={{ bgcolor: 'white', borderRadius: 1 }} />
        </Box>
      )}

      {/* Selfie preview */}
      {selfiePreview && (
        <Stack direction="row" alignItems="center" spacing={1.5} mb={2}
          sx={{ p: 1.5, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
          <Avatar src={selfiePreview} sx={{ width: 52, height: 52, border: '2px solid #16a34a' }} />
          <Box>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <CheckCircle sx={{ fontSize: 14, color: '#16a34a' }} />
              <Typography fontSize={13} color="#16a34a" fontWeight={700}>Selfie captured ✓</Typography>
            </Stack>
            <Typography fontSize={11} color="text.secondary">Sent to your Approval Manager</Typography>
          </Box>
        </Stack>
      )}

      {/* Clock buttons */}
      {status === 'OUT' && (
        <Button fullWidth variant="contained" color="success" size="large"
          startIcon={actionLoading ? <CircularProgress size={22} color="inherit" /> : <PlayArrow />}
          disabled={actionLoading}
          onClick={() => startAction('in')}
          sx={{ py: 2.2, fontWeight: 900, fontSize: 17, borderRadius: 3, mb: 1.5 }}>
          {actionLoading ? actionStep : '▶  CLOCK IN'}
        </Button>
      )}

      {status === 'IN' && (
        <Button fullWidth variant="contained" color="error" size="large"
          startIcon={actionLoading ? <CircularProgress size={22} color="inherit" /> : <Stop />}
          disabled={actionLoading}
          onClick={() => startAction('out')}
          sx={{ py: 2.2, fontWeight: 900, fontSize: 17, borderRadius: 3, mb: 1.5 }}>
          {actionLoading ? actionStep : '⏹  CLOCK OUT'}
        </Button>
      )}

      {status === 'OUT_DONE' && (
        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 3, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <Typography fontSize={15} fontWeight={700} color="#16a34a">
            ✅ Attendance marked for today
          </Typography>
          <Typography fontSize={12} color="text.secondary" mt={0.5}>
            See you tomorrow!
          </Typography>
        </Paper>
      )}

      <Typography fontSize={11} color="text.secondary" textAlign="center" mt={2}>
        🔒 GPS verified · Fake GPS blocked · Selfie required
      </Typography>

      <CameraModal open={cameraOpen} onCapture={handleWebSelfie} onClose={() => setCameraOpen(false)} />
    </Box>
  );
};

export default MobileClockPage;